import { defineStore } from 'pinia'
import { STORE_ID } from '~/constants/store'
import type { SavedAddress, SavedGiftCard, SavedVehicle, UserDataState as State } from '~/types/userData'

const MAX_HISTORY_ITEMS = 5

const initialState = (): State => ({
  savedAddresses: [],
  savedGiftCards: [],
  savedVehicles: [],
  shouldSaveGiftCardDetails: false
})

/**
 * @description Persisted state belonging to the signed-in user. Reset on sign out.
 */
const useUserDataStore = defineStore(STORE_ID.USER_DATA, {
  state: initialState,
  actions: {
    saveGiftCard(giftCard: SavedGiftCard): void {
      const normalisedCode = giftCard.code.trim()

      this.savedGiftCards = [
        { code: normalisedCode, pin: giftCard.pin },
        ...this.savedGiftCards.filter(
          (savedGiftCard) => savedGiftCard.code.toLowerCase() !== normalisedCode.toLowerCase()
        )
      ].slice(0, MAX_HISTORY_ITEMS)
    },
    removeSavedGiftCard(code: string): void {
      this.savedGiftCards = this.savedGiftCards.filter(
        (savedGiftCard) => savedGiftCard.code.toLowerCase() !== code.toLowerCase()
      )
    },
    clearSavedGiftCards(): void {
      this.savedGiftCards = []
    },
    setShouldSaveGiftCardDetails(value: boolean): void {
      this.shouldSaveGiftCardDetails = value
    },
    saveVehicle(vehicle: SavedVehicle): void {
      const normalisedRegistration = vehicle.registration.trim().toUpperCase()
      const existingVehicle = this.savedVehicles.find(
        (savedVehicle) => savedVehicle.registration.toLowerCase() === normalisedRegistration.toLowerCase()
      )

      this.savedVehicles = [
        { ...existingVehicle, ...vehicle, registration: normalisedRegistration },
        ...this.savedVehicles.filter(
          (savedVehicle) => savedVehicle.registration.toLowerCase() !== normalisedRegistration.toLowerCase()
        )
      ].slice(0, MAX_HISTORY_ITEMS)
    },
    removeSavedVehicle(registration: string): void {
      this.savedVehicles = this.savedVehicles.filter(
        (savedVehicle) => savedVehicle.registration.toLowerCase() !== registration.toLowerCase()
      )
    },
    saveAddress(address: SavedAddress): void {
      const normalisedFullAddress = address.fullAddress.trim()

      this.savedAddresses = [
        { ...address, fullAddress: normalisedFullAddress },
        ...this.savedAddresses.filter(
          (savedAddress) => savedAddress.fullAddress.toLowerCase() !== normalisedFullAddress.toLowerCase()
        )
      ].slice(0, MAX_HISTORY_ITEMS)
    },
    removeSavedAddress(fullAddress: string): void {
      this.savedAddresses = this.savedAddresses.filter(
        (savedAddress) => savedAddress.fullAddress.toLowerCase() !== fullAddress.toLowerCase()
      )
    }
  },
  persist: {
    afterHydrate: ({ store }) => {
      store.savedAddresses = store.savedAddresses.slice(0, MAX_HISTORY_ITEMS)
      store.savedGiftCards = store.savedGiftCards.slice(0, MAX_HISTORY_ITEMS)
      store.savedVehicles = store.savedVehicles.slice(0, MAX_HISTORY_ITEMS)
      store.$persist()
    }
  }
})

export default useUserDataStore
